import { groupSetsByExercise, calculateSessionStats } from '../utils/ExerciseUtils';

/**
 * Analizza il progresso degli esercizi
 * @param {Array} sets - Serie di dati degli esercizi
 * @param {Function} calculate1RM - Funzione per calcolare 1RM
 * @param {Function} calculateWeightRange - Funzione per calcolare il range di peso consigliato
 * @returns {Object} Oggetto con progressi degli esercizi
 */
export function calculateExerciseProgress(sets, calculate1RM, calculateWeightRange) {
  // Raggruppa le serie per esercizio
  const exerciseMap = groupSetsByExercise(sets);

  // Calcola il progresso per ogni esercizio
  const progressData = {};
  
  for (const exerciseId in exerciseMap) {
    const exercise = exerciseMap[exerciseId];
    
    if (exercise.sets.length < 2) continue;
    
    // Ordina i set per data
    exercise.sets.sort((a, b) => a.date - b.date);
    
    // Raggruppa i set per sessione di allenamento (per data)
    const setsByWorkout = {};
    exercise.sets.forEach(set => {
      const dateStr = set.date.toISOString().substring(0, 10); // YYYY-MM-DD
      if (!setsByWorkout[dateStr]) {
        setsByWorkout[dateStr] = [];
      }
      setsByWorkout[dateStr].push(set);
    });
    
    // Ottieni date ordinate degli allenamenti
    const workoutDates = Object.keys(setsByWorkout).sort();
    
    // Calcola 1RM per ogni sessione di allenamento
    const firstWorkoutSets = setsByWorkout[workoutDates[0]];
    const lastWorkoutSets = setsByWorkout[workoutDates[workoutDates.length - 1]];
    
    // Calcola 1RM, peso massimo e media per la prima e ultima sessione
    const firstSessionData = calculateSessionStats(firstWorkoutSets);
    const lastSessionData = calculateSessionStats(lastWorkoutSets);
    
    // Calcola il cambiamento percentuale nel 1RM
    const rmChange = lastSessionData.estimatedRM - firstSessionData.estimatedRM;
    const rmChangePercent = firstSessionData.estimatedRM > 0 
      ? (rmChange / firstSessionData.estimatedRM) * 100 
      : 0;
    
    // Calcola il progresso settimanale medio
    const firstDate = new Date(workoutDates[0]);
    const lastDate = new Date(workoutDates[workoutDates.length - 1]);
    const weeksBetween = Math.max(1, Math.round((lastDate - firstDate) / (7 * 24 * 60 * 60 * 1000)));
    const weeklyProgressRate = weeksBetween > 0 ? rmChange / weeksBetween : 0;
    
    // Formatta i pesi correttamente
    const formatWeight = (weight) => {
      return weight < 10 ? `${weight.toFixed(1)} kg` : `${weight.toFixed(0)} kg`;
    };
    
    // Calcola range di peso consigliato
    const weightRange = calculateWeightRange(lastSessionData.estimatedRM, exercise.type);
    
    progressData[exercise.name] = {
      initialWeight: formatWeight(firstSessionData.maxWeight),
      currentWeight: formatWeight(lastSessionData.maxWeight),
      initialRM: formatWeight(firstSessionData.estimatedRM),
      currentRM: formatWeight(lastSessionData.estimatedRM),
      maxWeight: formatWeight(lastSessionData.maxWeight),
      averageWeight: formatWeight(lastSessionData.averageWeight),
      weightChange: rmChange.toFixed(1),
      weightChangePercent: parseFloat(rmChangePercent.toFixed(1)),
      weeklyProgressRate: parseFloat(weeklyProgressRate.toFixed(2)),
      timePeriod: weeksBetween,
      suggestedWeightRange: weightRange.formatted,
      weightRangeDetails: weightRange,
      lastWorkoutDetails: {
        date: lastDate.toISOString().substring(0, 10),
        sets: lastWorkoutSets.length,
        maxWeight: formatWeight(lastSessionData.maxWeight),
        averageWeight: formatWeight(lastSessionData.averageWeight),
        estimatedRM: formatWeight(lastSessionData.estimatedRM)
      }
    };
  }

  // Ordina per percentuale di cambiamento
  return Object.entries(progressData)
    .sort((a, b) => b[1].weightChangePercent - a[1].weightChangePercent)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
}